import React from 'react'
import styled, {css} from 'styled-components'
import {gsap} from 'gsap'
import {ScrollTrigger} from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

import {
  workshops,
  ButtonAdapter,
  Content,
  Headline,
  LazyImageAdapter,
} from '@miro-site/components_utils'

import type {Assets} from '@miro-site/components_utils'
import {Box, Section, Video, Desktop} from '@miro-site/core'
import type {StyleProps, BoxProps} from '@miro-site/core'

type BackgroundType = keyof typeof WORKSHOPS_BG_COLORS
type HeadlineType = React.ComponentProps<typeof Headline>

const {WORKSHOPS_BG_COLORS} = workshops

interface HeroSecondLevelProps {
  background?: BackgroundType
  headline?: HeadlineType
  button?: React.ComponentProps<typeof ButtonAdapter>
  content?: React.ComponentProps<typeof Content>
  shape?: Assets
}

type ImageAndVideoProps =
  | {
      image: Assets
      video?: never
    }
  | {
      video: React.ComponentProps<typeof Video>
      image?: never
    }

const SECTION_STYLES: StyleProps = {
  position: 'relative',
  overflow: 'hidden',
  height: [null, 830],
  marginBottom: [48, 88],
}

interface ColProps extends BoxProps {
  $isFirstColumn?: boolean
}

// * &::nth-child doesn't work 🤨
const Col = styled(Box).attrs({
  $py: [null, 0],
  $px: [null, 10],
})<ColProps>`
  ${({$isFirstColumn = true}) => {
    const FIRST_COLUMN_WIDTH = '41.66%'
    const SECOND_COLUMN_WIDTH = '58.34%'
    return $isFirstColumn
      ? css`
          width: ${FIRST_COLUMN_WIDTH};
          flex: 0 0 ${FIRST_COLUMN_WIDTH};
        `
      : css`
          width: ${SECOND_COLUMN_WIDTH};
          flex: 0 0 ${SECOND_COLUMN_WIDTH};
        `
  }}

  ${({theme}) => css`
    ${theme.mq.small(css`
      width: 100%;
      flex: 0 0 100%;
    `)}
  `};
`

const Row = styled(Box).attrs({
  $my: 0,
  $mx: [null, -10],
})`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
`

const VerticalContent = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`

const AssetsContainer = styled(Box).attrs({
  $mt: [40, null],
  $mx: [-20, null],
  $mb: [0, null],
  $py: [39, null],
  $px: [20, null],
})`
  position: relative;
`

const ShapeContainer = styled(Box).attrs({
  $styles: {
    width: [null, 1120],
    position: ['relative', 'absolute'],
    left: [0, null],
    transform: ['translateX(0)', 'translateX(-50%)'],
  },
})`
  top: 0;
  bottom: 0;
  left: 50%;
`

const Shape = styled(Box).attrs({
  $styles: {
    width: [365, 990],
    left: ['50%', '33.33%'],
    marginLeft: [null, 10],
    transform: ['translateX(-50%)', 'none'],
  },
})`
  height: 100%;
  position: absolute;
  top: 0;
`

const ImageContainer = styled(Box).attrs({
  $styles: {
    borderRadius: [4],
    width: [325, 800],
  },
})`
  height: auto;
  max-height: 100%;
  display: block;
  position: relative;
  z-index: 3;
  overflow: hidden;

  ${({theme}) => css`
    ${theme.mq.small(css`
      box-shadow: 0 ${theme.multipliers.small(theme.spaces.sm)}
        ${theme.multipliers.small(theme.spaces.lg)} rgba(5, 0, 56, 0.12);
    `)}
    ${theme.mq.mediumDown(css`
      box-shadow: 0 ${theme.multipliers.mediumDown(theme.spaces.sm)}
        ${theme.multipliers.mediumDown(theme.spaces.lg)} rgba(5, 0, 56, 0.12);
    `)}
    ${theme.mq.mediumUp(css`
      box-shadow: 0 ${theme.multipliers.mediumUp(theme.spaces.sm)}
        ${theme.multipliers.mediumUp(theme.spaces.lg)} rgba(5, 0, 56, 0.12);
    `)}
    ${theme.mq.large(css`
      box-shadow: 0 ${theme.multipliers.large(theme.spaces.sm)}
        ${theme.multipliers.large(theme.spaces.lg)} rgba(5, 0, 56, 0.12);
    `)}
  `};
`

const VideoContainer = styled(Box).attrs(({theme}) => {
  return {
    $styles: {
      borderRadius: [theme.spaces.xxs, theme.spaces.sm],
      width: [325, 800],
    },
  }
})`
  position: relative;
  overflow: hidden;
`

const ButtonContainer = styled(Box).attrs(({theme}) => {
  return {
    $mt: [theme.spaces.lg, theme.spaces.xl],
  }
})``

const getHeadline = (headlineProps: HeadlineType, background: BackgroundType = '') => {
  Object.assign(headlineProps, {
    size: headlineProps.size || 'medium',
    tag: headlineProps.tag || 'h1',
    color: workshops.isHighContrastColor(background) ? 'white' : headlineProps.color,
    align: headlineProps.align || 'left',
  })

  return <Headline {...headlineProps}></Headline>
}

const HeroSecondLevel: React.FC<HeroSecondLevelProps & ImageAndVideoProps> = ({
  headline,
  background = 'workshops-color-grey',
  button,
  content,
  shape,
  image,
  video,
}) => {
  const ref = React.useRef<HTMLElement>(null)
  const shapeRef = React.useRef<HTMLElement>(null)
  const textColor = workshops.isHighContrastColor(background)
    ? 'white'
    : background == ''
    ? 'dark'
    : null

  React.useEffect(() => {
    if (!ref.current || !shapeRef.current) {
      return
    }

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: ref.current,
        scrub: true,
        start: '-100px top',
        end: 'bottom top',
      },
    })

    tl.fromTo(shapeRef.current, {y: 0}, {y: -70})
  }, [ref, shapeRef])

  return (
    <Section
      data-place="Hero Section"
      ref={ref}
      $styles={SECTION_STYLES}
      $noPaddingTop={true}
      $noPaddingBottom={true}
      type="large"
      background={WORKSHOPS_BG_COLORS[background]}
    >
      <Box
        $styles={{
          maxWidth: [null, 1120],
          zIndex: 3,
          margin: 'auto',
          position: 'relative',
        }}
        $pt={[40, 120]}
        $pb={[20, 80]}
        $px={[20, null]}
      >
        <Row>
          <Col>
            <VerticalContent>
              <div>
                {headline?.text && getHeadline(headline)}
                {button && ( // make the contextual space lg xl through theme
                  <ButtonContainer>
                    <ButtonAdapter
                      {...button}
                      type={!background || background == 'workshops-color-grey' ? 'blue' : 'white'}
                    />
                  </ButtonContainer>
                )}
              </div>
              <Box $mt={[64, 48]}>
                {content && <Content {...content} color={textColor || content.color}></Content>}
              </Box>
            </VerticalContent>
          </Col>
          <Col $isFirstColumn={false}>
            <AssetsContainer>
              {image && (
                <ImageContainer>
                  <LazyImageAdapter assets={image}></LazyImageAdapter>
                </ImageContainer>
              )}
              {shape?.mobile && (
                <Shape>
                  <LazyImageAdapter assets={{mobile: shape.mobile}}></LazyImageAdapter>
                </Shape>
              )}
              {video && (
                <VideoContainer>
                  <Video id={video.id} thumbnail={video.thumbnail}></Video>
                </VideoContainer>
              )}
            </AssetsContainer>
          </Col>
        </Row>
      </Box>
      {shape?.desktop && (
        <Desktop>
          <ShapeContainer>
            <Shape data-testid="shape-test-id" ref={shapeRef}>
              <LazyImageAdapter assets={{desktop: shape.desktop}} />
            </Shape>
          </ShapeContainer>
        </Desktop>
      )}
    </Section>
  )
}

export default HeroSecondLevel
